import * as THREE from 'three';

import { type Fixture, type FixtureKind, TV } from '../config/booth';
import { footprintLabel } from '../config/feet';
import { BRAND, font, makeCanvas, makeTexture } from '../textures/canvas';

const NAMES: Record<FixtureKind, string> = {
  counter: 'Counter',
  kiosk: 'Demo pedestal',
  closet: 'Storage closet',
  tv: `TV stand (${TV.diagonalInches}" panel)`,
};

/** A flat text tag drawn on the floor, read from the plan camera. */
function tag(text: string): THREE.Mesh {
  const ctx = makeCanvas(1024, 128);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = font('display', 60);
  ctx.fillStyle = BRAND.red;
  ctx.fillText(text, 512, 64);
  const h = 0.9;
  const m = new THREE.Mesh(
    new THREE.PlaneGeometry(h * 8, h),
    new THREE.MeshBasicMaterial({
      map: makeTexture(ctx),
      transparent: true,
      depthWrite: false,
    }),
  );
  m.rotation.x = -Math.PI / 2;
  return m;
}

/** The fixture's footprint, as a red outline a hair above the floor. */
function outline(f: Fixture): THREE.LineLoop {
  const w = f.w / 2;
  const d = f.d / 2;
  const geo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-w, 0, -d),
    new THREE.Vector3(w, 0, -d),
    new THREE.Vector3(w, 0, d),
    new THREE.Vector3(-w, 0, d),
  ]);
  return new THREE.LineLoop(
    geo,
    new THREE.LineBasicMaterial({ color: BRAND.red }),
  );
}

export interface FixtureDims {
  group: THREE.Group;
  /** Moves a fixture's outline and tag with it. */
  place(f: Fixture): void;
  setVisible(id: string, on: boolean): void;
}

/**
 * Plan-view dimensions for the fixtures that can be dragged: each gets its
 * footprint outlined and a tag naming it with width × depth, just in front of
 * it so the tag never sits on the fixture's own graphics.
 */
export function buildFixtureDims(fixtures: Fixture[]): FixtureDims {
  const group = new THREE.Group();
  const roots = new Map<string, THREE.Group>();
  for (const f of fixtures) {
    const root = new THREE.Group();
    const line = outline(f);
    line.position.y = f.h + 0.05;
    root.add(line);
    const t = tag(`${NAMES[f.kind]}  ${footprintLabel(f.w, f.d)}`);
    t.position.set(0, f.h + 0.06, f.d / 2 + 0.6);
    root.add(t);
    root.position.set(f.x, 0, f.z);
    root.rotation.y = f.rot;
    roots.set(f.id, root);
    group.add(root);
  }
  return {
    group,
    place(f) {
      const root = roots.get(f.id);
      if (!root) return;
      root.position.set(f.x, 0, f.z);
      root.rotation.y = f.rot;
    },
    setVisible(id, on) {
      const root = roots.get(id);
      if (root) root.visible = on;
    },
  };
}
